import { HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2 } from 'elements/hardwareFilter/constantsV2'
import { GLOBAL_SUPPLIER_NODE_KEY_V2 } from 'elements/hardwareFilter/distributor/GlobalDistributorFilterFactory'
import createAccordionRenderer from 'elements/hardwareFilter/renderers/createAccordionRenderer'
import { AVAILABILITY_FILTER_KEY_V2 } from 'elements/hardwareFilter/stockAvailability/AvailabilityFilterNodeFactoryV2'
import type {
  ConfigurableFilterOptionsType,
  FilterGenericPropsType,
  FilterKeysV2Type,
  FilterValuesType,
} from 'elements/hardwareFilter/typeV2'
import { getLastRecentUsedFilters } from 'elements/hardwareFilter/utils/lruFilterCacheStore'
import { VIEW_FROM_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/viewFrom/ViewFromFilterNodeFactoryV2'
import { ComponentVersionsInherit } from 'opensolar-ui'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import React, { useMemo } from 'react'
import { ComponentTypesV2 } from '../../../types/selectComponent'
import { useFeatureFlag } from '../../../util/featureFlags'
import { SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2 } from '../../hardwareFilter/systemCompatibility/SystemCompatibilityFilterNodeFactoryV2'
import { useHardwareSelectorFilterNodes } from '../useHardwareSelectorFilterNodes'
import { HardwareSelectorFilterSidebarContext } from './HardwareSelectorFilterSidebarContext'

const HardwareSelectorFilterSidebar = ({
  hardwareFilterConfigList = HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2,
  persistentFilterValues,
  modifiableFilterValues,
  allFilterValues,
  setModifiableFilterValues,
  componentTypes,
  location,
  onFiltersReady,
}: {
  hardwareFilterConfigList?: ConfigurableFilterOptionsType<any, any>[]
  componentTypes?: ComponentTypesV2[]
  persistentFilterValues: FilterValuesType
  modifiableFilterValues: FilterValuesType
  allFilterValues: FilterValuesType
  setModifiableFilterValues(
    newFilterValues: FilterValuesType,
    displayedFilters?: { [key in FilterKeysV2Type]?: boolean },
    immediate?: boolean
  ): void
  location: 'shop' | 'design'
  onFiltersReady?: () => void
}) => {
  const enabledHardwareShopV3 = useFeatureFlag('enable_multi_distributor_shop', 'on')
  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()
  const controlVersion = enabledHardwareShopV3 ? 2 : 1

  const expandedFilters = [
    VIEW_FROM_FILTER_NODE_KEY_V2,
    GLOBAL_SUPPLIER_NODE_KEY_V2,
    AVAILABILITY_FILTER_KEY_V2,
    SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2,
  ]

  const filtersConfigs = useMemo(() => {
    return hardwareFilterConfigList
      .filter((filter) => !(hardwareDistributors.length === 0 && filter.key === GLOBAL_SUPPLIER_NODE_KEY_V2))
      .map(({ key, rendererComponent, ...rest }, index, array) => {
        const isLast = index === array.length - 1
        return {
          key,
          rendererComponentWithAccordion: createAccordionRenderer({
            BasicRenderer: rendererComponent,
            filterKey: key,
            options: {
              defaultExpanded: expandedFilters.includes(key) || !!getLastRecentUsedFilters()?.includes(key),
              showDivider: !isLast,
              recordLru: true,
            },
          }) as typeof rendererComponent,
          ...rest,
        }
      })
  }, [hardwareFilterConfigList])

  const { filterNodes } = useHardwareSelectorFilterNodes({
    filtersConfigs,
    allFilterValues,
    componentTypes,
    setModifiableFilterValues,
    onFiltersReady,
  })

  return (
    <HardwareSelectorFilterSidebarContext.Provider value={{ location }}>
      <div>
        {filterNodes.map(({ key, component }, index: number) => {
          const Filter = component as React.FC<FilterGenericPropsType>

          return (
            <ComponentVersionsInherit key={key} versions={{ checkbox: controlVersion, radio: controlVersion }}>
              <Filter
                key={key}
                persistentFilterValues={persistentFilterValues}
                modifiableFilterValues={modifiableFilterValues}
                allFilterValues={allFilterValues}
                setModifiableFilters={setModifiableFilterValues}
                componentTypes={componentTypes}
              />
            </ComponentVersionsInherit>
          )
        })}
      </div>
    </HardwareSelectorFilterSidebarContext.Provider>
  )
}

export default React.memo(HardwareSelectorFilterSidebar)
